export const helpCenterContent = {
  'en-US': {
    title: 'Help Center (QA)',
    searchPlaceholder: 'Search questions...',
    noResultsForQuery: 'No results match your search.',
    noResultsDefault: 'No help content is available yet.',
    categories: [
      {
        id: 'getting-started',
        title: 'Getting started',
        items: [
          {
            id: 'q1',
            question: 'How do I create a simulation?',
            answer:
              'Go to the "Simulation History" page and click the "Create Simulation" button in the top-right corner. Fill in the required parameters—such as asset collections, strategy, time range, trading cost, and stop-loss ratio—then click "Run Simulation" or "Save as Draft". You can find step-by-step instructions in the in-app tutorial.'
          },
          {
            id: 'q1a',
            question: 'What is an asset collection?',
            answer:
              'An asset collection is a group of securities (such as stocks or futures) that you curate for simulations. You can create and manage collections in the "My Collections" module and use them as the universe of tradable symbols during simulations.'
          }
        ]
      },
      {
        id: 'running-simulations',
        title: 'Running simulations',
        items: [
          {
            id: 'q2',
            question: 'What is maximum drawdown?',
            answer:
              'Maximum drawdown measures the largest drop in account value from a peak to a subsequent trough within a selected period. It helps evaluate potential loss risk. For example, if equity rises from 100 to 150, falls to 75, and then climbs to 200, the maximum drawdown is the decline from 150 to 75, which equals (150 - 75) / 150 = 50%.'
          },
          {
            id: 'q3',
            question: 'What should I do if a simulation fails?',
            answer:
              'When a simulation fails the system usually provides a reason. Check whether the parameters are valid (for example, date ranges and selected assets), and confirm that your network connection is stable. Common causes include data source issues, invalid parameters, or strategies that cannot execute under specific conditions. If the issue persists, contact us through the feedback portal and include the simulation ID and error message.'
          },
          {
            id: 'q3a',
            question: 'Why is the win rate high but the strategy still loses money?',
            answer:
              'A high win rate only means profitable trades occur more often. If losses per trade are significantly larger than gains (i.e., the payoff ratio is poor), the strategy can still end up losing money overall. Evaluate multiple metrics together, such as win rate, payoff ratio, and the Sharpe ratio.'
          }
        ]
      },
      {
        id: 'collections',
        title: 'My collections',
        items: [
          {
            id: 'q4',
            question: 'How do I add assets to a collection?',
            answer:
              'In the "My Collections" module, click "Create Collection" or select an existing collection and click "Edit". Use the search box to find assets by symbol or name, select the ones you want to add, and save your changes when finished.'
          }
        ]
      },
      {
        id: 'watchlist',
        title: 'My watchlist',
        items: [
          {
            id: 'q5',
            question: 'What is the "My Watchlist" feature?',
            answer:
              'The watchlist lets you keep track of collections and strategies you care about so you can monitor their performance and information in one place. You can pin frequently used collections or strategies for quick access when creating new simulations.'
          },
          {
            id: 'q5a',
            question: 'How do I add a new watchlist item?',
            answer:
              'Open "My Watchlist" from the navigation bar and click "Add to Watchlist". Choose whether you want to follow asset collections or strategies, select the specific entries from the list, and click "Add".'
          },
          {
            id: 'q5b',
            question: 'How do I start a simulation from a watchlist item?',
            answer:
              'Each watchlist item has a "Create Simulation" button. Click it to open the Simulation History page with the relevant collection or strategy preselected, then fill in the remaining parameters to launch the simulation quickly.'
          },
          {
            id: 'q5c',
            question: 'How do I remove an item from the watchlist?',
            answer:
              'On the "My Watchlist" page, click the "×" icon in the top-right corner of the item you want to remove. Confirm the action to unfollow it. Removing an item does not affect any simulations you previously created with it.'
          }
        ]
      }
    ]
  },
  'zh-CN': {
    title: '帮助中心 (QA)',
    searchPlaceholder: '搜索问题...',
    noResultsForQuery: '未能找到与您搜索条件匹配的结果。',
    noResultsDefault: '暂无帮助内容。',
    categories: [
      {
        id: 'getting-started',
        title: '新手入门',
        items: [
          {
            id: 'q1',
            question: '如何创建模拟？',
            answer:
              '要创建模拟，请导航到"模拟历史"页面，点击右上角的"创建模拟"按钮，填写所需参数（如标的集合、策略、时间周期、交易成本、止损比例等），然后点击"运行模拟"或"保存为草稿"。详细步骤请参考应用内的模拟创建教程或指引。'
          },
          {
            id: 'q1a',
            question: '什么是标的物集合？',
            answer:
              '标的物集合是您自定义的一组金融资产（如股票、期货），用于在模拟交易中作为可选的交易对象池。您可以在"我的收藏"模块中创建和管理这些集合。'
          }
        ]
      },
      {
        id: 'running-simulations',
        title: '模拟运行',
        items: [
          {
            id: 'q2',
            question: '最大回撤是什么？',
            answer:
              '最大回撤是指在选定周期内，账户净值从前期最高点回落到最低点的最大幅度。它通常用来衡量策略可能面临的最大资金损失风险。例如，如果账户从100元涨到150元，然后跌到75元，再涨到200元，则最大回撤发生在从150元跌至75元，回撤幅度为(150-75)/150 = 50%。'
          },
          {
            id: 'q3',
            question: '模拟失败怎么办？',
            answer:
              '如果模拟失败，系统通常会给出失败原因。请首先检查您输入的参数是否正确（例如，日期范围是否合理，所选标的是否在该时段有数据），网络连接是否稳定。常见的失败原因包括：数据源问题、参数配置错误、或策略在特定条件下无法执行。如果问题持续，请通过用户反馈渠道联系我们，并提供模拟ID和错误信息截图。'
          },
          {
            id: 'q3a',
            question: '为什么我的胜率很高但最终亏损？',
            answer:
              '胜率高仅代表盈利次数占比较多，但如果单次亏损的金额远大于单次盈利的金额（即盈亏比过低），即使胜率高也可能导致总体亏损。建议综合评估胜率、盈亏比、夏普比率等多个指标。'
          }
        ]
      },
      {
        id: 'collections',
        title: '我的收藏',
        items: [
          {
            id: 'q4',
            question: '如何将标的添加到我的收藏集？',
            answer:
              '在“我的收藏”模块中，您可以点击“创建新收藏集”或选择一个现有收藏集并点击“编辑”。在编辑/创建页面，您可以通过搜索框输入标的代码或名称来查找资产，然后在搜索结果中选择您想添加的标的，它们将被加入到当前操作的收藏集中。完成后记得保存。'
          }
        ]
      },
      {
        id: 'watchlist',
        title: '我的关注',
        items: [
          {
            id: 'q5',
            question: '什么是"我的关注"功能？',
            answer:
              '"我的关注"功能允许您关注感兴趣的资产集合和交易策略，方便您随时跟踪它们的表现和信息。您可以将常用的集合和策略添加到关注列表中，快速访问并用于创建新的模拟交易。'
          },
          {
            id: 'q5a',
            question: '如何添加新的关注项目？',
            answer:
              '点击导航栏中的"我的关注"，然后点击"添加新关注"按钮。选择您想要关注的类型（资产集合或交易策略），从可用列表中选择具体项目，最后点击"添加到关注列表"即可。'
          },
          {
            id: 'q5b',
            question: '如何使用关注的项目创建模拟？',
            answer:
              '在"我的关注"页面中，每个关注项目都有一个"创建模拟"按钮。点击该按钮将跳转到模拟历史页面，并自动预选相应的集合或策略，您只需填写其他参数即可快速开始模拟。'
          },
          {
            id: 'q5c',
            question: '如何取消关注某个项目？',
            answer:
              '在"我的关注"页面中，每个关注项目的右上角都有一个"×"按钮。点击该按钮并确认即可取消关注该项目。取消关注不会影响您之前使用该项目创建的模拟记录。'
          }
        ]
      }
    ]
  }
};
